export type TrafficChannel =
  | 'direct'
  | 'organic'
  | 'social'
  | 'email'
  | 'paid'
  | 'referral';

export type UtmParams = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
};

const PAID_MEDIUMS = new Set([
  'cpc',
  'ppc',
  'paid',
  'paidsocial',
  'paid_social',
  'paid-social',
  'display',
  'cpm',
  'retargeting',
  'ads',
]);

const EMAIL_MEDIUMS = new Set(['email', 'e-mail', 'newsletter', 'mail']);

const SOCIAL_MEDIUMS = new Set([
  'social',
  'social-network',
  'social_network',
  'social-media',
  'social_media',
  'sm',
]);

const ORGANIC_MEDIUMS = new Set(['organic', 'seo']);

const SOCIAL_HOSTS = [
  'facebook.com',
  'fb.com',
  'instagram.com',
  'linkedin.com',
  'lnkd.in',
  'twitter.com',
  'x.com',
  't.co',
  'tiktok.com',
  'youtube.com',
  'youtu.be',
  'pinterest.com',
  'reddit.com',
  'whatsapp.com',
  'telegram.org',
  't.me',
];

const SEARCH_HOSTS = [
  'google.',
  'bing.com',
  'yahoo.',
  'duckduckgo.com',
  'baidu.com',
  'yandex.',
];

const normalize = (value: string | null | undefined): string | undefined => {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed;
};

const normalizeLower = (value: string | null | undefined): string | undefined =>
  normalize(value)?.toLowerCase();

export const getUtmParams = (
  search: string = typeof window !== 'undefined' ? window.location.search : ''
): UtmParams => {
  const params = new URLSearchParams(search);
  const utms: UtmParams = {};

  const source = normalize(params.get('utm_source'));
  const medium = normalize(params.get('utm_medium'));
  const campaign = normalize(params.get('utm_campaign'));
  const content = normalize(params.get('utm_content'));

  if (source) utms.utm_source = source;
  if (medium) utms.utm_medium = medium;
  if (campaign) utms.utm_campaign = campaign;
  if (content) utms.utm_content = content;

  return utms;
};

const getReferrerHost = (
  referrer: string = typeof document !== 'undefined' ? document.referrer : ''
): string | undefined => {
  if (!referrer) return undefined;
  try {
    return new URL(referrer).hostname.toLowerCase();
  } catch {
    return undefined;
  }
};

const isSameSiteReferrer = (referrerHost: string): boolean => {
  if (typeof window === 'undefined') return false;
  const currentHost = window.location.hostname.toLowerCase();
  return (
    referrerHost === currentHost ||
    referrerHost.endsWith(`.${currentHost}`) ||
    currentHost.endsWith(`.${referrerHost}`)
  );
};

const matchesHost = (referrerHost: string, patterns: string[]): boolean =>
  patterns.some(
    (pattern) =>
      referrerHost === pattern ||
      referrerHost.endsWith(`.${pattern}`) ||
      referrerHost.includes(pattern)
  );

export const deriveTrafficChannel = (
  utms: UtmParams,
  referrer: string = typeof document !== 'undefined' ? document.referrer : ''
): TrafficChannel => {
  const medium = normalizeLower(utms.utm_medium);
  const source = normalizeLower(utms.utm_source);

  if (medium && PAID_MEDIUMS.has(medium)) return 'paid';
  if (medium && EMAIL_MEDIUMS.has(medium)) return 'email';
  if (medium && SOCIAL_MEDIUMS.has(medium)) return 'social';
  if (medium && ORGANIC_MEDIUMS.has(medium)) return 'organic';

  if (source && (EMAIL_MEDIUMS.has(source) || source.includes('mail'))) {
    return 'email';
  }

  const referrerHost = getReferrerHost(referrer);
  if (referrerHost && !isSameSiteReferrer(referrerHost)) {
    if (matchesHost(referrerHost, SOCIAL_HOSTS)) return 'social';
    if (matchesHost(referrerHost, SEARCH_HOSTS)) return 'organic';
    return 'referral';
  }

  if (utms.utm_source || utms.utm_medium || utms.utm_campaign || utms.utm_content) {
    return 'referral';
  }

  return 'direct';
};

export type ReportSourceProperties = {
  traffic_channel: TrafficChannel;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
};

export type ReportOpenedProperties = {
  traffic_channel: TrafficChannel;
  traffic_source?: string;
  traffic_medium?: string;
};

export const buildReportTrafficProperties = (
  search?: string,
  referrer?: string
): {
  sourceIdentified: ReportSourceProperties;
  opened: ReportOpenedProperties;
} => {
  const utms = getUtmParams(search);
  const trafficChannel = deriveTrafficChannel(utms, referrer);

  const sourceIdentified: ReportSourceProperties = {
    traffic_channel: trafficChannel,
    ...utms,
  };

  const opened: ReportOpenedProperties = {
    traffic_channel: trafficChannel,
  };

  if (utms.utm_source) opened.traffic_source = utms.utm_source;
  if (utms.utm_medium) opened.traffic_medium = utms.utm_medium;

  return { sourceIdentified, opened };
};
